import type { ItemEvent } from '../../domain/models';
import { eventComment, eventNote, eventReason, eventTaskId } from '../../domain/models';
import type { Translate } from '../../l10n/i18n';
import type { Locale } from '../../l10n/messages';

// The history's shape, as in the mobile app (history_view_model.dart): events grouped by the local
// calendar day, newest day first.

export interface Day {
  /** Midnight, local time. */
  day: Date;
  events: ItemEvent[];
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Events (newest first) grouped by local calendar day, in the order they came. */
export function byDay(events: ItemEvent[]): Day[] {
  const days = new Map<number, Day>();
  for (const e of events) {
    const day = startOfDay(e.createdAt);
    const group = days.get(day.getTime());
    if (group) group.events.push(e);
    else days.set(day.getTime(), { day, events: [e] });
  }
  return [...days.values()];
}

/** "Šiandien", "Vakar", or the date in the person's language ("spalio 7 d."); the year only when it
 * is not this one. */
export function dayLabel(t: Translate, locale: Locale, day: Date, now = new Date()): string {
  const today = startOfDay(now);
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (day.getTime() === today.getTime()) return t('historyToday');
  if (day.getTime() === yesterday.getTime()) return t('historyYesterday');
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: day.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  }).format(day);
}

/** Hours and minutes, 24-hour, as the mobile app shows them. */
export const timeOfDay = (locale: Locale, at: Date) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);

/** What a person wrote with the event: a comment, a note or a reason. */
export function eventWords(e: ItemEvent): string | undefined {
  const words = eventComment(e) ?? eventNote(e) ?? eventReason(e);
  return words?.trim() ? words : undefined;
}

/** The item an event opens: a handoff opens the task it started, as the item's timeline does. */
export const eventTarget = (e: ItemEvent) => (e.kind === 'handed_off' ? (eventTaskId(e) ?? e.itemId) : e.itemId);
